import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import Auth from "./pages/Auth";
import Dashboard from "./pages/recruiter/Dashboard";
import JobManagement from "./pages/recruiter/JobManagement";
import JobKanban from "./pages/recruiter/JobKanban";
import ClientManagement from "./pages/recruiter/ClientManagement";
import ClientContactsManagement from "./pages/recruiter/ClientContactsManagement";
import CompanyManagement from "./pages/recruiter/CompanyManagement";
import ContactsManagement from "./pages/recruiter/ContactsManagement";
import CandidateManagement from "./pages/recruiter/CandidateManagement";
import UserRoleManagement from "./pages/admin/UserRoleManagement";
import SkillsManagement from "./pages/recruiter/SkillsManagement";
import ActivityLogs from "./pages/recruiter/ActivityLogs";
import NotificationCenter from "./pages/recruiter/NotificationCenter";
import NotificationAdmin from "./pages/recruiter/NotificationAdmin";
import EmailInbox from "./pages/recruiter/EmailInbox";
import PerformanceDashboard from "./pages/recruiter/PerformanceDashboard";

import CompanyIntelligence from "./pages/CompanyIntelligence";
import ClientDetail from "./pages/ClientDetail";

import CandidateApplications from "./pages/CandidateApplications";
import SharedCandidateProfile from "./pages/SharedCandidateProfile";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminLayout from "./components/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AppSettings from "./pages/admin/AppSettings";
import ProfileSettings from "./pages/admin/ProfileSettings";

import EmailSettings from "./pages/admin/EmailSettings";
import ScoringSettings from "./pages/admin/ScoringSettings";
import ErrorManagement from "./pages/admin/ErrorManagement";
import EntitySchemaViewer from "./pages/admin/EntitySchemaViewer";
import ParsingSettings from "./pages/admin/ParsingSettings";
import IntegrationsSettings from "./pages/admin/IntegrationsSettings";
import DataQuality from "./pages/admin/DataQuality";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Public routes - Candidate facing */}
            <Route path="/" element={<Index />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:jobId" element={<JobDetail />} />
            
            {/* Shared candidate profile (public with token) */}
            <Route path="/shared/candidate/:token" element={<SharedCandidateProfile />} />
            
            {/* Auth */}
            <Route path="/auth" element={<Auth />} />
            
            {/* Protected routes - Recruiter console */}
            <Route path="/recruiter" element={<ProtectedRoute />}>
              <Route index element={<Dashboard />} />
              <Route path="jobs" element={<JobManagement />} />
              <Route path="jobs/:jobId/kanban" element={<JobKanban />} />
              <Route path="clients" element={<ClientManagement />} />
              <Route path="clients/:clientId" element={<ClientDetail />} />
              <Route path="account-contacts" element={<ClientContactsManagement />} />
              <Route path="companies" element={<CompanyManagement />} />
              <Route path="companies/:companyId" element={<CompanyIntelligence />} />
              <Route path="contacts" element={<ContactsManagement />} />
              <Route path="candidates" element={<CandidateManagement />} />
              <Route path="candidates/:candidateId/applications" element={<CandidateApplications />} />
              <Route path="skills" element={<SkillsManagement />} />
              <Route path="notifications" element={<NotificationCenter />} />
              <Route path="inbox" element={<EmailInbox />} />
              <Route path="performance" element={<PerformanceDashboard />} />
            </Route>
            
            {/* Admin Panel - Admin only */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<UserRoleManagement />} />
              <Route path="masters" element={<EntitySchemaViewer />} />
              <Route path="activity-logs" element={<ActivityLogs />} />
              <Route path="data-quality" element={<DataQuality />} />
              <Route path="scoring" element={<ScoringSettings />} />
              <Route path="notifications" element={<NotificationAdmin />} />
              <Route path="email-settings" element={<EmailSettings />} />
              <Route path="errors" element={<ErrorManagement />} />
              <Route path="settings" element={<AppSettings />} />
              <Route path="profile" element={<ProfileSettings />} />
              <Route path="entity-schema" element={<EntitySchemaViewer />} />
              <Route path="parsing" element={<ParsingSettings />} />
              <Route path="integrations" element={<IntegrationsSettings />} />
            </Route>
            
            {/* Intelligence Routes (under companies) */}
            <Route path="/companies" element={<ProtectedRoute />}>
              <Route path=":companyId" element={<CompanyIntelligence />} />
            </Route>
            
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
